import { cacheConfiguration as useSurveyCacheConfig } from "@ameelio/use-survey";
import {
  ApolloClient,
  ApolloLink,
  HttpLink,
  InMemoryCache,
  StoreObject,
} from "@apollo/client";
import { Modifier } from "@apollo/client/cache";
import { SetContextLink } from "@apollo/client/link/context";
import { ErrorLink } from "@apollo/client/link/error";
import {
  getMainDefinition,
  relayStylePagination,
} from "@apollo/client/utilities";
import { Kind, OperationTypeNode } from "graphql";
import merge from "lodash.merge";
import fragmentMatcher from "./api/fragment-matcher.json";
import { getToken, handleAuthError } from "./lib/auth-client";

const { possibleTypes } = fragmentMatcher;

type Logout = (tokenError?: boolean) => void;
// Apollo Client 4 removed the `onError` callback from `useQuery` / `useLazyQuery`
// (kept only on `useMutation`). Query failures that call sites used to surface
// with `handleApolloError` are routed here instead, so every query gets the
// same toast + Sentry report without each call site wiring it up individually.
type Notify = (error: unknown) => void;

export const errorLink = (logout: Logout, notify: Notify) =>
  new ErrorLink(({ error, operation, forward }) => {
    if (
      handleAuthError(error, {
        logout,
        oryOrigin: import.meta.env.VITE_KRATOS_API_ORIGIN,
      })
    )
      return;

    // Surface unhandled query errors (toast + Sentry). Mutations keep their
    // own onError, so restricting this to queries avoids a double toast.
    const definition = getMainDefinition(operation.query);
    const isQuery =
      definition.kind === Kind.OPERATION_DEFINITION &&
      definition.operation === OperationTypeNode.QUERY;
    if (isQuery) {
      notify(error);
    }

    return forward(operation);
  });

const authLink = new SetContextLink((prevContext) => {
  const token = getToken();
  return {
    headers: {
      ...prevContext.headers,
      authorization: token,
    },
  };
});

const enhancedFetch = (
  _: URL | RequestInfo,
  init?: RequestInit
): Promise<Response> => {
  if (!init?.body) {
    throw new Error("GraphQL request must have a body");
  }
  const { operationName } = JSON.parse(init.body as string);
  return fetch(
    `${import.meta.env.VITE_API_ORIGIN}/api/graphql?op=${operationName}`,
    init
  );
};
const httpLink = new HttpLink({
  fetch: enhancedFetch,
  credentials: "include", // sends cookies with requests
});

const customNotificationPaginationPolicy = () => {
  const rsp = relayStylePagination();
  // Necessary type narrowing.
  if (typeof rsp.merge === "boolean")
    throw new Error(`relayStylePagination merge prop was set to bool somehow`);
  const defaultMerge = rsp.merge;
  const defaultRead = rsp.read;
  /**
   * We want to prevent requests that don't return any 'edges' from caching an empty edge set.
   * This occurs when we query for unreadNotificationCount (GetUnreadNotificationCount).
   * To prevent caching empty sets we merely update the existing cache with unreadNotificationCount
   * when 'edges' is missing.  Since 'edges' can be missing sometimes, we need to add an empty array
   * back when reading the cache as to not bork relayStylePagination.  It expects 'edges' to always exist.
   */
  return {
    ...rsp,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    read: (existing: any, opts: any) =>
      defaultRead?.(
        {
          ...existing,
          edges: existing?.edges || [],
        },
        opts
      ),
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    merge: (existing: any, incoming: any, opts: any) => {
      if (incoming && !incoming.edges) {
        return {
          ...existing,
          unreadNotificationCount:
            incoming?.unreadNotificationCount ??
            existing?.unreadNotificationCount,
        };
      }
      return defaultMerge?.(existing, incoming, opts);
    },
  };
};

const existingCacheConfiguration = {
  possibleTypes,
  typePolicies: {
    Visitor: {
      fields: {
        meetings: relayStylePagination(["dayStart"]),
        notificationEvents: customNotificationPaginationPolicy(),
      },
    },
    Inmate: {
      fields: {
        meetings: relayStylePagination(["dayStart"]),
        notificationEvents: customNotificationPaginationPolicy(),
      },
    },
    Connection: {
      fields: {
        timelineEvents: relayStylePagination(["@connection"]),
        fileGallery: relayStylePagination(["@connection"]),
      },
    },
  },
};

export const cacheConfiguration = merge(
  existingCacheConfiguration,
  useSurveyCacheConfig
);

const cache = new InMemoryCache(cacheConfiguration);

// Apollo Client 4 defaults notifyOnNetworkStatusChange to true, which makes
// polling (and any refetch/fetchMore) toggle `loading: true` and re-render.
// Restore the v3 default so background polling stays silent unless a query
// opts in explicitly.
const defaultOptions = {
  watchQuery: {
    notifyOnNetworkStatusChange: false,
  },
};

export const client = new ApolloClient({
  devtools: { enabled: import.meta.env.DEV },
  link: ApolloLink.from([httpLink]),
  cache,
  defaultOptions,
});

const getAuthenticatedClient = ({
  logout,
  notify,
}: {
  logout: Logout;
  notify: Notify;
}) =>
  new ApolloClient({
    devtools: { enabled: import.meta.env.DEV },
    link: ApolloLink.from([errorLink(logout, notify), authLink, httpLink]),
    cache,
    defaultOptions,
  });

export default getAuthenticatedClient;

/**
 * appendItem will return a cache modifier that adds an object to the end
 * of a list.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function appendItem(object: { id: string }): Modifier<any> {
  return (existing = [], { toReference, readField }) => {
    const existingArray = Array.isArray(existing) ? existing : [];
    const objectRef = toReference(object, true);
    if (!objectRef)
      throw new Error(
        "Attempted to insert an object with a faulty or missing id"
      );
    return [
      ...existingArray.filter((e) => object.id !== readField("id", e)),
      objectRef,
    ];
  };
}

export function evictItem(object: StoreObject & { id: string }) {
  cache.evict({ id: cache.identify(object) });
}
